import { useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import useSeo from '../../hooks/useSeo';
import { useTranslation } from '../../hooks/translations/TranslationProvider';

const DEFAULT_SITE_URL =
    process.env.REACT_APP_SITE_URL || 'https://sportwice.com';

const MIN_EVENTS_TO_INDEX_CITY = 0;

const normalizeCountryCode = (value: string | null) =>
    (value || '').trim().toUpperCase();

const normalizeCityName = (value: string | null) =>
    (value || '')
        .trim()
        .replace(/\s+/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());

type GroupedEvents = Array<{ events: unknown[] }>;

type UseFinderSeoArgs = {
    appliedCity: string;
    groupedEvents?: GroupedEvents;
};

export const useFinderSeo = ({ appliedCity, groupedEvents }: UseFinderSeoArgs) => {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { t } = useTranslation();

    const countryFromUrl = normalizeCountryCode(searchParams.get('country'));
    const cityFromUrl = normalizeCityName(searchParams.get('city'));

    const totalEvents = useMemo(
        () =>
            groupedEvents?.reduce(
                (count, group) => count + group.events.length,
                0
            ) || 0,
        [groupedEvents]
    );

    const hasCityParams = Boolean(countryFromUrl && cityFromUrl);
    const hasExtraParams = useMemo(
        () =>
            Array.from(searchParams.keys()).some(
                key => key !== 'country' && key !== 'city'
            ),
        [searchParams]
    );

    const canonicalCityUrl = useMemo(() => {
        if (!hasCityParams) {
            return undefined;
        }

        const normalizedParams = new URLSearchParams();
        normalizedParams.set('country', countryFromUrl);
        normalizedParams.set('city', cityFromUrl);

        return `${DEFAULT_SITE_URL}${location.pathname}?${normalizedParams.toString()}`;
    }, [countryFromUrl, cityFromUrl, hasCityParams, location.pathname]);

    const shouldIndexCityPage =
        hasCityParams &&
        !hasExtraParams &&
        totalEvents >= MIN_EVENTS_TO_INDEX_CITY;

    const noIndex = hasExtraParams || (hasCityParams && !shouldIndexCityPage);

    const seoCity = cityFromUrl || appliedCity;
    const baseTitle = t('seo.finder.title');
    const baseDescription = t('seo.finder.description');
    const seoTitle = seoCity ? `${baseTitle} | ${seoCity}` : baseTitle;
    const seoDescription = seoCity
        ? `${baseDescription} ${t('common.city')}: ${seoCity}.`
        : baseDescription;

    useSeo({
        title: seoTitle,
        description: seoDescription,
        path: '/finder',
        canonicalUrl: canonicalCityUrl,
        noIndex,
    });
};

export default useFinderSeo;
